import { parseCustomReportConfiguration } from "#batteries-included-components/Layouts/CustomReport/CustomReportConfigurationPanel/CustomReportConfigurationPanel.helpers";
import { CustomReportDetailLayout } from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout";
import {
  getCustomReportFilters,
  getCustomReportGroupBy,
  useSavedCustomReport,
  type CustomReportVariantType,
} from "#batteries-included-components/Layouts/CustomReport/CustomReportDetailLayout.helpers";
import { CreateCustomReportModal } from "#batteries-included-components/Modals/CustomReports/CreateCustomReportModal";
import { DeleteCustomReportModal } from "#batteries-included-components/Modals/CustomReports/DeleteCustomReportModal";
import { EditCustomReportModal } from "#batteries-included-components/Modals/CustomReports/EditCustomReportModal";
import { useNavigate, useParams } from "#routers/hooks";
import { CustomReportsListRoutePath } from "#routes/reports/custom-reports";
import { CustomReportsDetailRoutePath } from "#routes/reports/custom-reports/[reportVariant]/[reportId]/detail/index";
import { useAuthenticatedContext } from "#src/contexts/AuthenticatedContext.helpers";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbsFromRoute } from "#src/utils/route";
import {
  Button,
  DropdownMenu,
  Page,
  useFilters,
} from "@validereinc/common-components";
import { SavedFilterStatus, getFilterObject } from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import React, { useState } from "react";

export const CustomReportDetailPage = () => {
  const navigate = useNavigate();
  const { reportVariant = "records", reportId } =
    useParams<
      Exclude<typeof CustomReportsDetailRoutePath.pathParams, undefined>
    >();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [filterConfigStorageKey, tableConfigStorageKey, tableStateStorageKey] =
    useStorageKey(
      ["filter-configuration", "table-configuration", "table-state"],
      "custom-report-detail"
    );
  const [configStorageKey] = useStorageKey(
    ["custom-report-config"],
    "custom-report-detail"
  );
  const {
    v2: {
      userInfo: { checkHasPermissions },
    },
  } = useAuthenticatedContext();

  const canWriteCompanyReports = checkHasPermissions(
    "company_custom_reports:write"
  );
  const canDeleteCompanyReports = checkHasPermissions(
    "company_custom_reports:delete"
  );

  const [filters] = useFilters(filterConfigStorageKey);
  const [configuration] = useFilters(configStorageKey);

  const {
    data: savedFilter,
    query: { isLoading: isLoadingCustomReport },
  } = useSavedCustomReport(reportVariant as CustomReportVariantType, reportId);
  const [breadcrumbs] = useBreadcrumbsFromRoute(CustomReportsDetailRoutePath, {
    "/details": {
      title: savedFilter?.name,
    },
  });

  const { rows, assetType } = parseCustomReportConfiguration(configuration);
  const filter = getFilterObject(
    getCustomReportFilters(
      reportVariant as CustomReportVariantType,
      assetType,
      configuration,
      filters
    )
  );

  return (
    <Page
      category={`${toTitleCaseString(reportVariant, { intent: "ui" })} Custom Report`}
      title={savedFilter?.name ?? CustomReportsDetailRoutePath.title}
      breadcrumbs={breadcrumbs}
      isLoading={isLoadingCustomReport}
      actionRow={[
        ...(savedFilter?.status === SavedFilterStatus.DRAFT ||
        canWriteCompanyReports
          ? [
              <DropdownMenu
                key="edit-custom-report"
                options={[
                  {
                    label: "Overwrite",
                    onClick: () => {
                      setIsEditModalOpen(true);
                    },
                  },
                  {
                    label: "Save as New",
                    onClick: () => {
                      setIsCreateModalOpen(true);
                    },
                  },
                ]}
              >
                <Button
                  variant="primary"
                  icon="caret-down"
                  iconPosition="right"
                  isLoading={isEditModalOpen || isCreateModalOpen}
                >
                  Save Changes
                </Button>
              </DropdownMenu>,
            ]
          : []),
        ...(savedFilter?.status === SavedFilterStatus.DRAFT ||
        canDeleteCompanyReports
          ? [
              <Button
                key="delete-custom-report"
                variant="error-outline"
                onClick={() => setIsDeleteModalOpen(true)}
                isLoading={isDeleteModalOpen}
              >
                Delete
              </Button>,
            ]
          : []),
      ]}
    >
      <CustomReportDetailLayout
        configStorageKey={configStorageKey}
        reportVariant={reportVariant as CustomReportVariantType}
        filterConfigStorageKey={filterConfigStorageKey}
        tableConfigStorageKey={tableConfigStorageKey}
        tableStateStorageKey={tableStateStorageKey}
      />
      <CreateCustomReportModal
        variant={reportVariant as CustomReportVariantType}
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
        }}
        onSave={(data) => {
          navigate({
            ...CustomReportsDetailRoutePath.toLinkParts({
              pathParams: { reportId: data.id, reportVariant: reportVariant },
            }),
            state: data,
          });
        }}
        filter={filter}
        groupBy={getCustomReportGroupBy(
          reportVariant as CustomReportVariantType,
          assetType,
          rows
        )}
        metadata={[{ assetType }]}
      />
      <DeleteCustomReportModal
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
        onSuccess={() => {
          navigate(CustomReportsListRoutePath.toLinkParts());
        }}
        variant={reportVariant as CustomReportVariantType}
        customReportId={isDeleteModalOpen ? reportId : undefined}
      />
      <EditCustomReportModal
        onClose={() => setIsEditModalOpen(false)}
        customReportId={isEditModalOpen ? reportId : undefined}
        filter={filter}
        groupBy={getCustomReportGroupBy(
          reportVariant as CustomReportVariantType,
          assetType,
          rows
        )}
        name={savedFilter?.name}
        status={savedFilter?.status}
        variant={reportVariant as CustomReportVariantType}
        metadata={[{ assetType }]}
      />
    </Page>
  );
};
